import type {
    WeekdayShortName} from "@@/shared/parse-weekly-schedule";
import {
    weekdayLookup,
    weekdayShortNames,
} from "@@/shared/parse-weekly-schedule";
import { emptyArrayOf, emptyObjectOf } from "@@/utilities/empty";
import { createDateAgnosticTime } from "./create-date-agnostic-time";
import type { DateAgnosticTime } from "./date-agnostic-time";
import type {
    DateAgnosticTimeRange} from "./date-agnostic-time-range";
import {
    dateAgnosticTimeRangeFactory,
} from "./date-agnostic-time-range";
import type { WeekAvailability } from "./week-availability";

const getWeekdaySortNumber =
    (firstDay: WeekdayShortName) => (weekday: WeekdayShortName) => {
        const firstDayIndex = weekdayShortNames.findIndex(
            item => item === firstDay,
        );
        const originalDayIndex = weekdayShortNames.findIndex(
            item => item === weekday,
        );
        let dayIndex = originalDayIndex - firstDayIndex;
        dayIndex = dayIndex < 0 ? dayIndex + 7 : dayIndex;
        // console.log({
        //     dayIndex,
        //     firstDay,
        //     weekday,
        //     firstDayIndex,
        //     originalDayIndex,
        // });
        return dayIndex;
    };
const defaultEndTime: DateAgnosticTime = {
    hour: 23,
    minute: 59,
};
const parseDaysAndTimeRanges = (input: string): WeekAvailability => {
    const value = input.trim();
    const [days, timeRange] =
        value.indexOf(" ") && !/^\d{1,2}-\d{1,2}$/.test(value)
            ? value.toLocaleLowerCase().replace(/\s\s+/g, " ").split(" ")
            : ["mån-sön", value];

    const [startDay, endDay] = days.split("-");
    const [startTime, endTime] = timeRange?.split("-") ?? [];
    const [startHour, startMinute] = startTime?.split(":") ?? [];
    const [endHour, endMinute] = endTime?.split(":") ?? [];

    const startWeekdayShortName = weekdayLookup[
        startDay as keyof typeof weekdayLookup
    ] as WeekdayShortName; // mon
    const endWeekdayShortName = weekdayLookup[
        endDay as keyof typeof weekdayLookup
    ] as WeekdayShortName; // mon
    const startDayIndex = getWeekdaySortNumber("mon")(startWeekdayShortName); // 0 + 0
    const endDayIndex =
        endDay && endWeekdayShortName
            ? getWeekdaySortNumber("mon")(endWeekdayShortName)
            : startDayIndex; // 0 + 1

    // console.log("PARSE AV", {
    //     startDay,
    //     startDayIndex,
    //     startHour,
    //     startMinute,
    //     startWeekdayShortName,
    //     endDay,
    //     endDayIndex,
    //     endWeekdayShortName,
    //     endHour,
    //     endMinute,
    //     start: createDateAgnosticTime({
    //         hour: startHour,
    //         minute: startMinute,
    //     }),
    //     end: createDateAgnosticTime(
    //         {
    //             hour: endHour,
    //             minute: endMinute,
    //         },
    //         defaultEndTime,
    //     ),
    // });
    const timeRanges =
        typeof startHour === "undefined" || typeof endHour === "undefined"
            ? emptyArrayOf<DateAgnosticTimeRange>()
            : ([
                  dateAgnosticTimeRangeFactory(
                      createDateAgnosticTime({
                          hour: startHour,
                          minute: startMinute,
                      }),
                      createDateAgnosticTime(
                          {
                              hour: endHour,
                              minute: endMinute,
                          },
                          defaultEndTime,
                      ),
                  ),
              ].filter(Boolean) as DateAgnosticTimeRange[]);

    const daysWithinDayRange: number[] = [startDayIndex];
    let dayIndex = startDayIndex;
    if (startDayIndex !== endDayIndex) {
        do {
            // iterate from start day index
            // to end day index
            dayIndex = (dayIndex + 1) % 7;
            daysWithinDayRange.push(dayIndex);
            // console.log("add", dayIndex);
        } while (dayIndex !== endDayIndex);
    }
    // // eslint-disable-next-line no-console
    // console.log("days within range", {
    //     startDayIndex,
    //     endDayIndex,
    //     daysWithinDayRange,
    // });

    const result: WeekAvailability = {
        _type: "WEEK_AVAILABILITY",
        timezone: "Europe/Stockholm",
        week: {
            mon: (daysWithinDayRange.includes(0) && timeRanges) || [],
            tue: (daysWithinDayRange.includes(1) && timeRanges) || [],
            wed: (daysWithinDayRange.includes(2) && timeRanges) || [],
            thu: (daysWithinDayRange.includes(3) && timeRanges) || [],
            fri: (daysWithinDayRange.includes(4) && timeRanges) || [],
            sat: (daysWithinDayRange.includes(5) && timeRanges) || [],
            sun: (daysWithinDayRange.includes(6) && timeRanges) || [],
        },
    };

    return result;
};

const parseSchedule = (typedSchedule: string): WeekAvailability => {
    if (!typedSchedule.trim()) return emptyObjectOf<WeekAvailability>();
    const availability = typedSchedule
        .split(",")
        .map(item => item.trim().toLocaleLowerCase())
        .map(parseDaysAndTimeRanges)
        .reduce(
            (agg: WeekAvailability, next) => {
                weekdayShortNames.forEach(name => {
                    const nextHours = next.week[
                        name
                    ] as DateAgnosticTimeRange[];
                    agg.week[name] = [
                        ...(agg?.week?.[name] ?? []),
                        ...(nextHours || []),
                    ];
                });
                return agg;
            },
            {
                _type: "WEEK_AVAILABILITY",
                timezone: "Europe/Stockholm",
                week: {},
            } as WeekAvailability,
        );

    return availability;
};

export { parseSchedule };
